import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  ShieldCheck, User, Phone, Calendar, Mail, Users, Calculator,
  Loader2, CheckCircle2, XCircle, ArrowLeft, TrendingUp, FileSignature,
  Landmark, ThumbsUp, Lock, Wallet, PartyPopper,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e98e0869-fa23-475f-b903-619c95eaa713/images/f4ac101ccf8270d37cab592d896b904bcc33d0be4642927a9a58b283a3cce81c.png";

const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Mostra mensagem amigável ao cliente, sem vazar erros técnicos internos (login parceiro, tokens, etc)
const msgAmigavel = (detail, fallback) => {
  const m = String(detail || "");
  if (!m || /login|prospecta|token|unauthorized|credenc|bearer|500|502|503|timeout|internal/i.test(m)) return fallback;
  return m;
};

const maskCPF = (v) => v.replace(/\D/g, "").slice(0, 11)
  .replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const maskFone = (v) => v.replace(/\D/g, "").slice(0, 11)
  .replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
const maskData = (v) => v.replace(/\D/g, "").slice(0, 8)
  .replace(/(\d{2})(\d)/, "$1/$2").replace(/(\d{2})(\d)/, "$1/$2");

// input branded
const Campo = ({ icon: Icon, label, req, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-semibold text-slate-700">{label}{req && <span className="text-blue-600"> *</span>}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
      {children}
    </div>
  </div>
);

const inputCls = "w-full h-12 rounded-xl border border-slate-200 bg-slate-50/60 pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100";

export default function LandingSimulacaoCLT() {
  const [step, setStep] = useState("form"); // form | ofertas | digitar | sucesso
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ cpf: "", celular: "", nome: "", data_nascimento: "", nome_mae: "", email: "" });
  const [erro, setErro] = useState("");
  const [resultado, setResultado] = useState(null);
  const [oferta, setOferta] = useState(null); // {simulationId, offer}
  const [digit, setDigit] = useState({
    banco: "", agencia: "", conta: "", contaDigito: "", tipoConta: "CC",
    rg: "", orgao_emissor: "", uf_emissor: "", data_expedicao: "",
    endereco_cep: "", endereco_rua: "", endereco_numero: "", endereco_bairro: "", endereco_cidade: "", endereco_uf: "",
  });
  const [proposta, setProposta] = useState(null);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setD = (k, v) => setDigit((p) => ({ ...p, [k]: v }));

  // Registra o avanço do cliente no fluxo (best-effort, nunca bloqueia o usuário)
  const registrarEtapa = async (etapa, extra = {}) => {
    try {
      await axios.post(`${API}/prospecta/landing/progresso`, {
        cpf: form.cpf.replace(/\D/g, ""),
        etapa,
        nome: form.nome,
        celular: form.celular.replace(/\D/g, ""),
        data_nascimento: form.data_nascimento,
        nome_mae: form.nome_mae,
        email: form.email,
        ...extra,
      });
    } catch { /* silencioso */ }
  };

  const simular = async () => {
    setErro("");
    const cpfDigits = form.cpf.replace(/\D/g, "");
    if (cpfDigits.length !== 11) return setErro("Informe um CPF válido.");
    if (!form.nome.trim()) return setErro("Informe seu nome completo.");
    if (form.data_nascimento.replace(/\D/g, "").length !== 8) return setErro("Informe a data de nascimento (DD/MM/AAAA).");
    if (!form.nome_mae.trim()) return setErro("Informe o nome da sua mãe.");
    setLoading(true);
    // 1) captura o lead ANTES de simular (funciona mesmo se a API do parceiro cair)
    await registrarEtapa("PREENCHEU");
    try {
      const r = await axios.post(`${API}/prospecta/simular`, {
        cpf: cpfDigits, nome: form.nome, data_nascimento: form.data_nascimento,
        nome_mae: form.nome_mae, celular: form.celular.replace(/\D/g, ""), email: form.email,
      });
      setResultado(r.data);
      const bks = (r.data?.banks || []).filter((b) => (b.offers || []).length > 0);
      const best = bks.flatMap((b) => b.offers || []).reduce((mx, o) => Math.max(mx, Number(o.liquidAmount) || 0), 0);
      registrarEtapa("SIMULOU", best ? { valor_disponivel: best } : {});
      setStep("ofertas");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErro(msgAmigavel(e.response?.data?.detail, "No momento não foi possível concluir sua simulação. Tente novamente em instantes."));
    } finally { setLoading(false); }
  };

  const escolherOferta = async (simulationId, offer) => {
    setLoading(true);
    try {
      await axios.post(`${API}/prospecta/selecionar-oferta`, { simulationId, offerId: offer.offerId });
      setOferta({ simulationId, offer });
      registrarEtapa("ESCOLHEU_OFERTA", { detalhe: `${offer.installments}x de ${brl(offer.installmentAmount)} · líquido ${brl(offer.liquidAmount)}`, valor_disponivel: Number(offer.liquidAmount) || undefined });
      setStep("digitar");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErro(msgAmigavel(e.response?.data?.detail, "Não foi possível selecionar a oferta agora. Tente novamente."));
    } finally { setLoading(false); }
  };

  const digitar = async () => {
    setErro("");
    if (!digit.banco || !digit.agencia || !digit.conta) return setErro("Informe banco, agência e conta para receber o crédito.");
    setLoading(true);
    try {
      const cliente_extra = {
        rg: digit.rg, orgao_emissor: digit.orgao_emissor, uf_emissor: digit.uf_emissor, data_expedicao: digit.data_expedicao,
        endereco_cep: digit.endereco_cep, endereco_rua: digit.endereco_rua, endereco_numero: digit.endereco_numero,
        endereco_bairro: digit.endereco_bairro, endereco_cidade: digit.endereco_cidade, endereco_uf: digit.endereco_uf,
      };
      const banco_dados = { banco: digit.banco, agencia: digit.agencia, conta: digit.conta, contaDigito: digit.contaDigito, tipoConta: digit.tipoConta };
      const r = await axios.post(`${API}/prospecta/digitar`, {
        cpf: form.cpf.replace(/\D/g, ""), simulationId: oferta.simulationId, cliente_extra, banco_dados,
      });
      setProposta(r.data);
      registrarEtapa("DIGITOU", { detalhe: `Proposta ${r.data?.proposalNumber || ""}`.trim() });
      setStep("sucesso");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErro(msgAmigavel(e.response?.data?.detail, "Não foi possível enviar sua proposta agora. Tente novamente."));
    } finally { setLoading(false); }
  };

  const bancosComOferta = (resultado?.banks || []).filter((b) => (b.offers || []).length > 0);
  const semOferta = step === "ofertas" && bancosComOferta.length === 0;
  const melhorOferta = bancosComOferta[0]?.offers?.reduce((a, b) => (Number(b.liquidAmount) > Number(a.liquidAmount) ? b : a), bancosComOferta[0]?.offers?.[0]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-slate-50 to-emerald-50" data-testid="landing-clt">
      {/* Topbar */}
      <header className="w-full border-b border-white/60 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-black tracking-tight text-orange-500">UY3</span>
            <span className="h-6 w-px bg-slate-300" />
            <span className="text-xl font-extrabold tracking-tight"><span className="text-orange-500">pay</span><span className="text-amber-400">pro</span></span>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-500" /> Ambiente 100% seguro
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 lg:grid-cols-[1fr_1.1fr] lg:items-start">
        {/* Hero / lado esquerdo */}
        <section className="order-2 lg:order-1 lg:sticky lg:top-8">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 to-blue-500 p-1 shadow-xl">
            <div className="rounded-[22px] bg-white">
              <img src={HERO_IMG} alt="Crédito consignado CLT" className="h-56 w-full rounded-t-[22px] object-cover object-top sm:h-64" />
              <div className="p-6">
                <h2 className="text-2xl font-extrabold text-slate-800">Olá! 👋</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Você pode ter um <b className="text-blue-700">crédito consignado CLT pré-aprovado</b> no banco UY3 PayPró.
                  Consulte agora o valor disponível e veja se pode receber <b>em até 24 horas</b>.
                </p>
                <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                  {[[ThumbsUp, "Simples", "e rápido"], [Lock, "Seguro", "e confiável"], [Calendar, "Parcelas", "que cabem no bolso"]].map(([Ic, a, b], i) => (
                    <div key={i} className="rounded-xl bg-blue-50 p-3">
                      <Ic className="mx-auto h-5 w-5 text-blue-600" />
                      <p className="mt-1 text-xs font-bold text-slate-700">{a}</p>
                      <p className="text-[10px] text-slate-500">{b}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Card do fluxo / lado direito */}
        <section className="order-1 lg:order-2">
          <div className="rounded-3xl border border-white bg-white/90 p-6 shadow-xl backdrop-blur sm:p-8">
            {/* Header do card */}
            <div className="mb-6 flex flex-col items-center text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-500 shadow-lg shadow-blue-200">
                <Calculator className="h-7 w-7 text-white" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-800 sm:text-3xl">
                {step === "form" && "Simulação de Crédito Consignado CLT"}
                {step === "ofertas" && (semOferta ? "Resultado da sua consulta" : "Ofertas disponíveis para você")}
                {step === "digitar" && "Quase lá! Dados para o crédito"}
                {step === "sucesso" && "Proposta enviada com sucesso!"}
              </h1>
              <p className="mt-1 max-w-md text-sm text-slate-500">
                {step === "form" && "Preencha os dados abaixo para consultar o valor disponível para o seu perfil."}
                {step === "ofertas" && !semOferta && "Escolha a opção que melhor cabe no seu bolso."}
                {step === "digitar" && "Informe a conta para receber o valor e finalize a contratação."}
                {step === "sucesso" && "Enviamos sua proposta. Acompanhe os próximos passos abaixo."}
              </p>
            </div>

            {/* Stepper */}
            {step !== "sucesso" && (
              <div className="mb-6 flex items-center justify-center gap-2 text-[11px] font-semibold">
                {[["form", "Dados"], ["ofertas", "Ofertas"], ["digitar", "Contratação"]].map(([s, lab], i) => {
                  const idx = ["form", "ofertas", "digitar"].indexOf(step);
                  const active = i <= idx;
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                      <span className={active ? "text-blue-700" : "text-slate-400"}>{lab}</span>
                      {i < 2 && <span className="mx-1 text-slate-300">→</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {erro && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="landing-erro">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {erro}
              </div>
            )}

            {/* STEP FORM */}
            {step === "form" && (
              <div className="space-y-4" data-testid="step-form">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Campo icon={User} label="CPF" req>
                    <input className={inputCls} placeholder="000.000.000-00" inputMode="numeric" value={form.cpf}
                      onChange={(e) => setF("cpf", maskCPF(e.target.value))} data-testid="landing-cpf" />
                  </Campo>
                  <Campo icon={Phone} label="Celular">
                    <input className={inputCls} placeholder="(00) 00000-0000" inputMode="numeric" value={form.celular}
                      onChange={(e) => setF("celular", maskFone(e.target.value))} data-testid="landing-celular" />
                  </Campo>
                  <div className="sm:col-span-2">
                    <Campo icon={User} label="Nome completo" req>
                      <input className={inputCls} placeholder="Digite seu nome completo" value={form.nome}
                        onChange={(e) => setF("nome", e.target.value)} data-testid="landing-nome" />
                    </Campo>
                  </div>
                  <div className="sm:col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-[11rem_1fr]">
                    <Campo icon={Calendar} label="Nascimento" req>
                      <input className={inputCls} placeholder="DD/MM/AAAA" inputMode="numeric" value={form.data_nascimento}
                        onChange={(e) => setF("data_nascimento", maskData(e.target.value))} data-testid="landing-nasc" />
                    </Campo>
                    <Campo icon={Users} label="Nome da mãe" req>
                      <input className={inputCls} placeholder="Digite o nome da sua mãe" value={form.nome_mae}
                        onChange={(e) => setF("nome_mae", e.target.value)} data-testid="landing-mae" />
                    </Campo>
                  </div>
                  <div className="sm:col-span-2">
                    <Campo icon={Mail} label="E-mail">
                      <input className={inputCls} placeholder="Digite seu melhor e-mail" type="email" value={form.email}
                        onChange={(e) => setF("email", e.target.value)} data-testid="landing-email" />
                    </Campo>
                  </div>
                </div>
                <button onClick={simular} disabled={loading} data-testid="landing-simular-btn"
                  className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 text-lg font-bold text-white shadow-lg shadow-blue-200 transition-all hover:from-blue-700 hover:to-emerald-600 active:scale-[0.99] disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <TrendingUp className="h-5 w-5" />}
                  {loading ? "Consultando..." : "Simular agora"}
                </button>
                <p className="flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
                  <ShieldCheck className="h-3.5 w-3.5" /> Seus dados serão usados apenas para realizar sua simulação com segurança.
                </p>
              </div>
            )}

            {/* STEP OFERTAS */}
            {step === "ofertas" && (
              <div className="space-y-4" data-testid="step-ofertas">
                {semOferta ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="landing-sem-oferta">
                    <XCircle className="mx-auto mb-2 h-10 w-10 text-amber-400" />
                    <p className="font-bold text-amber-800">No momento não encontramos uma oferta disponível.</p>
                    <p className="mt-1 text-sm text-amber-700">
                      {resultado?.sem_margem ? "Não identificamos margem consignável disponível no seu perfil no momento."
                        : "Nenhum banco retornou oferta para o seu CPF agora. Tente novamente mais tarde."}
                    </p>
                    <button onClick={() => { setStep("form"); setErro(""); }} className="mt-4 text-sm font-semibold text-blue-600 underline">Refazer simulação</button>
                  </div>
                ) : (
                  <>
                    {melhorOferta && (
                      <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-emerald-500 p-5 text-white shadow-lg" data-testid="landing-destaque">
                        <div className="flex items-center gap-2 text-sm font-medium opacity-90"><Wallet className="h-4 w-4" /> Valor disponível para você</div>
                        <div className="mt-1 text-4xl font-black">{brl(melhorOferta.liquidAmount)}</div>
                        <div className="mt-1 text-sm opacity-90">em até {melhorOferta.installments}x de {brl(melhorOferta.installmentAmount)}</div>
                      </div>
                    )}
                    {bancosComOferta.map((bk) => (
                      <div key={bk.bankId} className="rounded-2xl border border-slate-200 p-4" data-testid={`landing-banco-${bk.bankId}`}>
                        <div className="mb-3 flex items-center gap-2 font-bold text-slate-700">
                          <Landmark className="h-4 w-4 text-blue-600" /> {bk.bankName || bk.bankId}
                        </div>
                        <div className="space-y-3">
                          {bk.offers.map((of) => (
                            <div key={of.offerId} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid={`landing-oferta-${of.offerId}`}>
                              <div className="mb-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
                                <div><p className="text-[11px] text-slate-400">Parcelas</p><p className="font-bold text-slate-700">{of.installments}x</p></div>
                                <div><p className="text-[11px] text-slate-400">Parcela</p><p className="font-bold text-slate-700">{brl(of.installmentAmount)}</p></div>
                                <div><p className="text-[11px] text-slate-400">Você recebe</p><p className="font-bold text-emerald-600">{brl(of.liquidAmount)}</p></div>
                                <div><p className="text-[11px] text-slate-400">Taxa a.m.</p><p className="font-bold text-slate-700">{of.interestRate}%</p></div>
                              </div>
                              <button onClick={() => escolherOferta(bk.simulationId, of)} disabled={loading} data-testid={`landing-escolher-${of.offerId}`}
                                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 font-semibold text-white transition-all hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-60">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Quero esta oferta
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => { setStep("form"); setErro(""); }} className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700">
                      <ArrowLeft className="h-4 w-4" /> Voltar
                    </button>
                  </>
                )}
              </div>
            )}

            {/* STEP DIGITAR */}
            {step === "digitar" && (
              <div className="space-y-4" data-testid="step-digitar">
                {oferta && (
                  <div className="rounded-xl bg-blue-50 p-3 text-sm text-blue-800">
                    <b>Oferta escolhida:</b> {oferta.offer.installments}x de {brl(oferta.offer.installmentAmount)} · você recebe {brl(oferta.offer.liquidAmount)}
                  </div>
                )}
                <p className="text-sm font-bold text-slate-700">Conta bancária para receber o crédito</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2"><Campo icon={Landmark} label="Banco" req><input className={inputCls} placeholder="Ex.: Itaú" value={digit.banco} onChange={(e) => setD("banco", e.target.value)} data-testid="landing-banco" /></Campo></div>
                  <Campo icon={Landmark} label="Agência" req><input className={inputCls} value={digit.agencia} onChange={(e) => setD("agencia", e.target.value)} data-testid="landing-agencia" /></Campo>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2"><Campo icon={Landmark} label="Conta" req><input className={inputCls} value={digit.conta} onChange={(e) => setD("conta", e.target.value)} data-testid="landing-conta" /></Campo></div>
                    <div><label className="text-sm font-semibold text-slate-700">Díg.</label><input className="mt-1.5 h-12 w-full rounded-xl border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.contaDigito} onChange={(e) => setD("contaDigito", e.target.value)} data-testid="landing-digito" /></div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Tipo de conta</label>
                    <select className="mt-1.5 h-12 w-full rounded-xl border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.tipoConta} onChange={(e) => setD("tipoConta", e.target.value)} data-testid="landing-tipo">
                      <option value="CC">Corrente</option><option value="CP">Poupança</option>
                    </select>
                  </div>
                </div>

                <p className="pt-2 text-sm font-bold text-slate-700">Documento e endereço</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div><label className="text-xs font-medium text-slate-600">RG</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.rg} onChange={(e) => setD("rg", e.target.value)} data-testid="landing-rg" /></div>
                  <div><label className="text-xs font-medium text-slate-600">Órgão emissor</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" placeholder="SSP" value={digit.orgao_emissor} onChange={(e) => setD("orgao_emissor", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">UF emissor</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" placeholder="SP" value={digit.uf_emissor} onChange={(e) => setD("uf_emissor", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Data de expedição</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" placeholder="DD/MM/AAAA" value={digit.data_expedicao} onChange={(e) => setD("data_expedicao", maskData(e.target.value))} /></div>
                  <div><label className="text-xs font-medium text-slate-600">CEP</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.endereco_cep} onChange={(e) => setD("endereco_cep", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Número</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.endereco_numero} onChange={(e) => setD("endereco_numero", e.target.value)} /></div>
                  <div className="sm:col-span-3"><label className="text-xs font-medium text-slate-600">Logradouro</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.endereco_rua} onChange={(e) => setD("endereco_rua", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Bairro</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.endereco_bairro} onChange={(e) => setD("endereco_bairro", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Cidade</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.endereco_cidade} onChange={(e) => setD("endereco_cidade", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">UF</label><input className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm outline-none focus:border-blue-500 focus:bg-white" value={digit.endereco_uf} onChange={(e) => setD("endereco_uf", e.target.value)} /></div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={() => { setStep("ofertas"); setErro(""); }} className="flex h-12 items-center gap-1 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    <ArrowLeft className="h-4 w-4" /> Voltar
                  </button>
                  <button onClick={digitar} disabled={loading} data-testid="landing-digitar-btn"
                    className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 font-bold text-white shadow-lg shadow-blue-200 transition-all hover:from-blue-700 hover:to-emerald-600 active:scale-[0.99] disabled:opacity-60">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileSignature className="h-5 w-5" />}
                    {loading ? "Enviando proposta..." : "Finalizar contratação"}
                  </button>
                </div>
              </div>
            )}

            {/* STEP SUCESSO */}
            {step === "sucesso" && proposta && (
              <div className="space-y-4 py-2 text-center" data-testid="step-sucesso">
                <PartyPopper className="mx-auto h-14 w-14 text-emerald-500" />
                <p className="text-lg font-bold text-slate-800">Sua proposta foi enviada!</p>
                {proposta.proposalNumber && (
                  <p className="text-sm text-slate-600">Número da proposta: <span className="font-mono font-bold text-slate-800">{proposta.proposalNumber}</span></p>
                )}
                {(proposta.statusNormalizado || proposta.status) && (
                  <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">{proposta.statusNormalizado || proposta.status}</span>
                )}
                {proposta.signUrl && (
                  <a href={proposta.signUrl} target="_blank" rel="noopener noreferrer" data-testid="landing-sign-link"
                    className="mx-auto flex h-12 w-full max-w-xs items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 font-bold text-white shadow-lg shadow-blue-200 hover:from-blue-700 hover:to-emerald-600">
                    <FileSignature className="h-5 w-5" /> Assinar contrato
                  </a>
                )}
                <p className="text-xs text-slate-400">Nossa equipe entrará em contato para concluir a liberação do seu crédito.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="pb-8 text-center text-xs text-slate-400">
        UY3 PayPró · Crédito Consignado CLT — Ambiente seguro 🔒
      </footer>
    </div>
  );
}
